import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { UserBookingSummaryItem, HttpError } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to fetch the user booking summary from backend.
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @returns {object} - Contains summary data, loading state, error state, and refetch function.
 */
function useFetchUserBookingSummary(dateFrom: Date, dateTo: Date) {
  const { t } = useTranslation();
  const [userBookingSummary, setUserBookingSummary] = useState<UserBookingSummaryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HttpError | null>(null);
  const { errorToast } = useShowToast();

  const fetchSummary = async (dateFrom: Date, dateTo: Date) => {
    if (!dateFrom || !dateTo) {
      setUserBookingSummary([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const summary = await api.getUserBookingSummary(dateFrom, dateTo);

      if (api.error) {
        setError(api.error);
        errorToast({
          title: t("club_management.user_bookings.fetch.error_title"),
          description: t("club_management.user_bookings.fetch.error_description"),
        });
      }

      setUserBookingSummary(summary || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  return {
    userBookingSummary,
    loading,
    error,
    refetchSummary: fetchSummary,
  };
}

export default useFetchUserBookingSummary;
